import React, { useState } from 'react';
import { X, Calendar, Clock, User, Mail, Phone, MessageSquare } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';

const BookingModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(1);
  const [bookingData, setBookingData] = useState({
    parentName: '',
    email: '',
    phone: '',
    childAge: '',
    preferredDate: '',
    preferredTime: '',
    message: ''
  });

  // Mock available dates (next 8 weeks, only Tue/Wed/Sat)
  // Vincolo: prenotazione con minimo 48 ore di anticipo
  const getAvailableDates = () => {
    const dates = [];
    const today = new Date();
    const minDate = new Date(today);
    minDate.setHours(minDate.getHours() + 48); // Minimo 48 ore di anticipo
    
    let daysChecked = 0;
    let currentDate = new Date(minDate);
    currentDate.setHours(0, 0, 0, 0); // Reset to start of day

    while (dates.length < 24 && daysChecked < 60) {
      currentDate.setDate(currentDate.getDate() + 1);
      daysChecked++;
      const dayOfWeek = currentDate.getDay();
      
      // Only Tuesday (2), Wednesday (3), and Saturday (6)
      if (dayOfWeek === 2 || dayOfWeek === 3 || dayOfWeek === 6) {
        dates.push(new Date(currentDate));
      }
    }
    return dates;
  };

  const availableDates = getAvailableDates();
  
  // Get available times based on selected day
  const getAvailableTimesForDay = (dateString) => {
    if (!dateString) return [];
    // Parse date correctly to avoid timezone issues
    const [year, month, day] = dateString.split('-');
    const date = new Date(year, month - 1, day);
    const dayOfWeek = date.getDay();
    
    // Tuesday (2) or Wednesday (3)
    if (dayOfWeek === 2 || dayOfWeek === 3) {
      return ['17:30', '18:00'];
    }
    // Saturday (6)
    if (dayOfWeek === 6) {
      return ['10:30', '11:00'];
    }
    return [];
  };
  
  const availableTimes = getAvailableTimesForDay(bookingData.preferredDate);

  const formatDate = (date) => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('it-IT', options);
  };

  const formatDateForInput = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (step === 1) {
      // Validate step 1
      if (!bookingData.parentName || !bookingData.email || !bookingData.phone) {
        toast.error('Compila tutti i campi obbligatori');
        return;
      }
      setStep(2);
    } else {
      // Validate step 2 and submit
      if (!bookingData.preferredDate || !bookingData.preferredTime) {
        toast.error('Seleziona data e orario');
        return;
      }
      
      // Call backend API
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      
      fetch(`${API_URL}/api/booking`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          parent_name: bookingData.parentName,
          email: bookingData.email,
          phone: bookingData.phone,
          child_age: bookingData.childAge,
          preferred_date: bookingData.preferredDate,
          preferred_time: bookingData.preferredTime,
          message: bookingData.message
        })
      })
      .then(response => response.json())
      .then(data => {
        toast.success(data.message || 'Prenotazione confermata!');
        
        // Reset and close
        setTimeout(() => {
          setBookingData({
            parentName: '',
            email: '',
            phone: '',
            childAge: '',
            preferredDate: '',
            preferredTime: '',
            message: ''
          });
          setStep(1);
          onClose();
        }, 1500);
      })
      .catch(error => {
        console.error('Error:', error);
        toast.error('Errore durante la prenotazione. Riprova.');
      });
    }
  };

  const handleChange = (e) => {
    setBookingData({
      ...bookingData,
      [e.target.name]: e.target.value
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto border-0 shadow-2xl">
        <CardContent className="p-0">
          {/* Header */}
          <div className="bg-gradient-to-r from-pink-500 to-purple-600 p-6 text-white relative">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <Calendar className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Prenota una Visita</h2>
                <p className="text-pink-100 text-sm">
                  Step {step} di 2: {step === 1 ? 'I tuoi dati' : 'Scegli data e ora'}
                </p>
              </div>
            </div>

            {/* Progress bar */}
            <div className="w-full bg-white/20 rounded-full h-2 mt-4">
              <div 
                className="bg-white rounded-full h-2 transition-all duration-300"
                style={{ width: step === 1 ? '50%' : '100%' }}
              ></div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6">
            {step === 1 ? (
              // Step 1: Contact Information
              <div className="space-y-4">
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    <User className="w-4 h-4 text-pink-600" />
                    Nome e Cognome *
                  </label>
                  <Input
                    name="parentName"
                    type="text"
                    required
                    value={bookingData.parentName}
                    onChange={handleChange}
                    placeholder="Il vostro nome"
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    <Mail className="w-4 h-4 text-pink-600" />
                    Email *
                  </label>
                  <Input
                    name="email"
                    type="email"
                    required
                    value={bookingData.email}
                    onChange={handleChange}
                    placeholder="vostra.email@esempio.com"
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    <Phone className="w-4 h-4 text-pink-600" />
                    Telefono *
                  </label>
                  <Input
                    name="phone"
                    type="tel"
                    required
                    value={bookingData.phone}
                    onChange={handleChange}
                    placeholder="333 1234567"
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    <User className="w-4 h-4 text-pink-600" />
                    Età del Bambino
                  </label>
                  <Input
                    name="childAge"
                    type="text"
                    value={bookingData.childAge}
                    onChange={handleChange}
                    placeholder="Es: 6 mesi, 1 anno..."
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    <MessageSquare className="w-4 h-4 text-pink-600" />
                    Note o Richieste Particolari
                  </label>
                  <Textarea
                    name="message"
                    rows={3}
                    value={bookingData.message}
                    onChange={handleChange}
                    placeholder="Eventuali domande o informazioni aggiuntive..."
                    className="w-full"
                  />
                </div>
              </div>
            ) : (
              // Step 2: Date & Time Selection
              <div className="space-y-6">
                {/* Info vincolo 48 ore */}
                <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg">
                  <div className="flex items-start gap-3">
                    <Calendar className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-blue-900 mb-1">
                        Anticipo minimo richiesto
                      </p>
                      <p className="text-sm text-blue-700">
                        Le prenotazioni devono essere effettuate con almeno <strong>48 ore di anticipo</strong>. 
                        Sono disponibili solo i martedì, mercoledì e sabato.
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-3">
                    <Calendar className="w-4 h-4 text-pink-600" />
                    Seleziona la Data *
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {availableDates.slice(0, 12).map((date, index) => {
                      const dateValue = formatDateForInput(date);
                      const isSelected = bookingData.preferredDate === dateValue;
                      return (
                        <button
                          key={index}
                          type="button"
                          onClick={() => setBookingData({ ...bookingData, preferredDate: dateValue })}
                          className={`p-3 rounded-lg border-2 text-left transition-all ${
                            isSelected
                              ? 'border-pink-600 bg-pink-50 shadow-md'
                              : 'border-gray-200 hover:border-pink-300 hover:bg-pink-50/50'
                          }`}
                        >
                          <p className="text-xs text-gray-500 capitalize">
                            {date.toLocaleDateString('it-IT', { weekday: 'short' })}
                          </p>
                          <p className={`font-semibold ${isSelected ? 'text-pink-600' : 'text-gray-900'}`}>
                            {date.getDate()} {date.toLocaleDateString('it-IT', { month: 'short' })}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {bookingData.preferredDate && (
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-3">
                      <Clock className="w-4 h-4 text-pink-600" />
                      Seleziona l'Orario *
                    </label>
                    <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
                      {availableTimes.map((time, index) => {
                        const isSelected = bookingData.preferredTime === time;
                        return (
                          <button
                            key={index}
                            type="button"
                            onClick={() => setBookingData({ ...bookingData, preferredTime: time })}
                            className={`p-3 rounded-lg border-2 text-center font-semibold transition-all ${
                              isSelected
                                ? 'border-pink-600 bg-pink-50 text-pink-600 shadow-md'
                                : 'border-gray-200 hover:border-pink-300 hover:bg-pink-50/50 text-gray-700'
                            }`}
                          >
                            {time}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {bookingData.preferredDate && bookingData.preferredTime && (
                  <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-xl p-4 border-2 border-pink-200">
                    <p className="text-sm text-gray-600 mb-2">Riepilogo prenotazione:</p>
                    <p className="font-semibold text-gray-900">
                      📅 {formatDate(new Date(bookingData.preferredDate))}
                    </p>
                    <p className="font-semibold text-gray-900">
                      🕐 Ore {bookingData.preferredTime} - {
                        bookingData.preferredTime === '17:30' ? '18:00' :
                        bookingData.preferredTime === '18:00' ? '18:30' :
                        bookingData.preferredTime === '10:30' ? '11:00' :
                        bookingData.preferredTime === '11:00' ? '11:30' : ''
                      }
                    </p>
                    <p className="text-sm text-gray-600 mt-2">
                      👤 {bookingData.parentName}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Buttons */}
            <div className="flex gap-3 mt-6">
              {step === 2 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep(1)}
                  className="flex-1 border-2 border-gray-300 hover:bg-gray-50"
                >
                  Indietro
                </Button>
              )}
              <Button
                type="submit"
                className="flex-1 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white shadow-lg hover:shadow-xl transition-all"
              >
                {step === 1 ? 'Continua' : 'Conferma Prenotazione'}
              </Button>
            </div>

            {step === 1 && (
              <p className="text-xs text-gray-500 text-center mt-4">
                * Campi obbligatori
              </p>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default BookingModal;
